import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import DataTable from '../components/common/DataTable';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {fmtDate,fmtDateTime} from '../utils/helpers';

const HI_TYPES=['OPConsultation','Prescription','DiagnosticReport','DischargeSummary','ImmunizationRecord','WellnessRecord'];
const PURPOSE_CODES=['CAREMGT','BTG','PUBHLTH','HPAYMT','DSRCH','PATRQT','COVAUTH','CLINTRCH','TREAT'];
const PURPOSE_TEXT:Record<string,string>={CAREMGT:'Care Management',BTG:'Break the Glass',PUBHLTH:'Public Health Activity',HPAYMT:'Healthcare Payment',DSRCH:'Disease Specific Healthcare Research',PATRQT:'Patient Requested',TREAT:'Treatment'};
const CONSENT_STATUS_COLORS:Record<string,string>={REQUESTED:'bg-amber-100 text-amber-700',GRANTED:'bg-green-100 text-green-700',DENIED:'bg-red-100 text-red-700',REVOKED:'bg-slate-100 text-slate-500',EXPIRED:'bg-red-50 text-red-400'};

export default function ABDMPage(){
  const [tab,setTab]=useState<'dashboard'|'abha'|'linking'|'consent'|'records'|'discovery'|'sharing'|'facility'|'hpr'|'logs'>('dashboard');
  const [stats,setStats]=useState<any>({});
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState(false);

  // ABHA Management
  const [abhaPatient,setAbhaPatient]=useState<any>(null);
  const [abhaRecord,setAbhaRecord]=useState<any>(null);
  const [showCreateABHA,setShowCreateABHA]=useState(false);
  const [abhaStep,setAbhaStep]=useState<'init'|'otp'|'create'>('init');
  const [aadhaar,setAadhaar]=useState('');
  const [mobile,setMobile]=useState('');
  const [txnId,setTxnId]=useState('');
  const [otp,setOtp]=useState('');
  const [showLinkForm,setShowLinkForm]=useState(false);
  const [linkForm,setLinkForm]=useState({abhaNumber:'',abhaAddress:'',linkMethod:'OTP',
    consentArtefactId:'',
    purposeDescription:'',
    grantedAt:'',
    revokedAt:'',
    accessModes:'VIEW',
    requestUrl:'',
    requestBody:'',
    responseBody:'',
    linkedBy:'',
    unlinkedAt:'',
    unlinkReason:'',
    profilePhoto:'',
    districtCode:'',
    consentToken:'',
    tokenExpiry:'',
    linkedAt:'',
    careContextReference:'',
    careContextDisplay:'',
    linkedConsentId:''
  });

  // Patient Links
  const [links,setLinks]=useState<any[]>([]);

  // Consent
  const [consents,setConsents]=useState<any[]>([]);
  const [artefacts,setArtefacts]=useState<any[]>([]);
  const [showConsentForm,setShowConsentForm]=useState(false);
  const [consentPatient,setConsentPatient]=useState<any>(null);
  const [consentForm,setConsentForm]=useState({patientId:'',abhaNumber:'',purposeCode:'CAREMGT',hiTypes:['OPConsultation','Prescription'] as string[],dateFrom:'',dateTo:'',expiryDate:'',hipId:'',hiuId:''});

  // Health Records
  const [recordPatient,setRecordPatient]=useState<any>(null);
  const [hiType,setHiType]=useState('OPConsultation');
  const [records,setRecords]=useState<any[]>([]);
  const [pushingRecord,setPushingRecord]=useState('');


  // Discovery
  const [discPatient,setDiscPatient]=useState<any>(null);
  const [discoveries,setDiscoveries]=useState<any[]>([]);
  const [showDiscForm,setShowDiscForm]=useState(false);
  const [discForm,setDiscForm]=useState({patientId:'',abhaNumber:'',dateFrom:'',dateTo:'',hiTypes:['OPConsultation','Prescription'] as string[]});

  // Sharing
  const [sharing,setSharing]=useState<any[]>([]);

  // Facility & HPR
  const [facilities,setFacilities]=useState<any[]>([]);
  const [hprList,setHprList]=useState<any[]>([]);
  const [showHPRForm,setShowHPRForm]=useState(false);
  const [hprForm,setHprForm]=useState({doctorId:'',hprId:'',registrationNumber:'',registrationCouncil:'Medical Council of India',speciality:'',qualification:''});
  const [doctors,setDoctors]=useState<any[]>([]);

  // Logs
  const [logs,setLogs]=useState<any[]>([]);
  const [logFilter,setLogFilter]=useState('');

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  useEffect(()=>{
    api.getABDMStats().then(r=>setStats(r.data||{})).catch(()=>{});
    api.getDoctors().then(r=>setDoctors(r.data||[])).catch(()=>{});
  },[]);

  const loadTab=useCallback(async()=>{
    setLoading(true);
    try{
      if(tab==='abha'||tab==='linking'){if(abhaPatient?.id){const [ab,lk]=await Promise.all([api.getABHAByPatient(abhaPatient.id),api.getAbhaLinks({patientId:abhaPatient.id})]);setAbhaRecord(ab.data);setLinks(lk.data||[]);}else if(tab==='linking'){const r=await api.getAbhaLinks();setLinks(r.data||[]);}}
      if(tab==='consent'){const [c,a]=await Promise.all([api.getConsents(),api.getConsentArtefacts()]);setConsents(c.data||[]);setArtefacts(a.data||[]);}
      if(tab==='records'){if(recordPatient?.id&&hiType){const r=await api.listHealthRecords(recordPatient.id,hiType);setRecords(r.data||[]);}}
      if(tab==='discovery'){const r=await api.getDiscoveries();setDiscoveries(r.data||[]);}
      if(tab==='sharing'){const r=await api.getSharingHistory();setSharing(r.data||[]);}
      if(tab==='facility'){const r=await api.getAbdmFacilities();setFacilities(r.data||[]);}
      if(tab==='hpr'){const r=await api.getHPRProfiles();setHprList(r.data||[]);}
      if(tab==='logs'){const r=await api.getAbdmLogs({status:logFilter||undefined});setLogs(r.data||[]);}
    }catch{}finally{setLoading(false);}
  },[tab,abhaPatient,recordPatient,hiType,logFilter]);
  useEffect(()=>{loadTab();},[loadTab]);

  // ABHA flow
  const generateOTP=async()=>{
    if(!aadhaar||!mobile){notify('⚠️ Enter Aadhaar and mobile number');return;}
    setSaving(true);
    try{const r=await api.generateABHAOTP({aadhaarNumber:aadhaar,mobile});setTxnId(r.data.txnId||'demo_txn');setAbhaStep('otp');notify('✅ OTP sent to mobile');}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Could not initiate — check ABDM credentials'));}
    finally{setSaving(false);}
  };
  const verifyOTP=async()=>{
    setSaving(true);
    try{const r=await api.verifyABHAOTP({txnId,otp});setTxnId(r.data.txnId||txnId);setAbhaStep('create');notify('✅ OTP verified!');}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'OTP verification failed'));}
    finally{setSaving(false);}
  };
  const createABHA=async()=>{
    if(!abhaPatient?.id){notify('⚠️ Select a patient first');return;}
    setSaving(true);
    try{
      const r=await api.createABHA({txnId});
      if(r.data?.abhaNumber){await api.linkABHA(abhaPatient.id,{abhaNumber:r.data.abhaNumber,abhaAddress:r.data.abhaAddress,name:r.data.name});await api.createAbhaLink({patientId:abhaPatient.id,abhaNumber:r.data.abhaNumber,abhaAddress:r.data.abhaAddress,linkMethod:'AADHAAR_OTP'});}
      notify('✅ ABHA created and linked!');setShowCreateABHA(false);setAbhaStep('init');setAadhaar('');setMobile('');setOtp('');loadTab();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'ABHA creation failed'));}
    finally{setSaving(false);}
  };

  const linkExistingABHA=async(e:React.FormEvent)=>{
    e.preventDefault();
    if(!abhaPatient?.id){notify('⚠️ Select a patient first');return;}
    setSaving(true);
    try{
      await api.linkABHA(abhaPatient.id,linkForm);
      await api.createAbhaLink({patientId:abhaPatient.id,...linkForm});
      notify('✅ ABHA linked to patient!');setShowLinkForm(false);loadTab();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const submitConsent=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.requestConsent(consentForm);notify('✅ Consent request sent!');setShowConsentForm(false);setConsentPatient(null);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const pushRecord=async(recordId:string)=>{
    if(!recordPatient)return;setPushingRecord(recordId);
    try{await api.pushHealthRecord(recordPatient.id,hiType,recordId);notify('✅ Record pushed to ABDM gateway!');loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setPushingRecord('');}
  };

  const [fhirBundle,setFhirBundle]=useState<any>(null);
  const [fhirLoading,setFhirLoading]=useState(false);
  const [showFhirViewer,setShowFhirViewer]=useState(false);
  const [fhirRecordTitle,setFhirRecordTitle]=useState('');

  const previewFhir=async(record:any)=>{
    if(!recordPatient){notify('⚠️ Select a patient first');return;}
    setFhirLoading(true);setFhirBundle(null);
    setFhirRecordTitle(`${hiType} — ${record.final_diagnosis||record.request_number||record.summary_number||record.id?.slice(0,8)}`);
    try{
      const r=await api.generateHealthRecord(recordPatient.id,hiType,record.id);
      setFhirBundle(r.data);
      setShowFhirViewer(true);
    }catch(e:any){notify('❌ Failed to generate FHIR R4 bundle: '+(e.response?.data?.message||'Error'));}
    setFhirLoading(false);
  };

  const submitDiscovery=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.initiateDiscovery({...discForm,patientId:discPatient?.id||discForm.patientId});notify('✅ Discovery request submitted!');setShowDiscForm(false);setDiscPatient(null);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const submitHPR=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.registerHPR(hprForm);notify('✅ HPR registration saved!');setShowHPRForm(false);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const TABS=[['dashboard','🏠 Dashboard'],['abha','🆔 ABHA Management'],['linking','🔗 Patient Linking'],['consent','✅ Consents'],['records','📄 Health Records'],['discovery','🔍 Discovery'],['sharing','📤 Record Sharing'],['facility','🏥 Facility Registry'],['hpr','👨‍⚕️ HPR'],['logs','📋 Audit Logs']];

  return(
    <Layout title="ABDM — National Digital Health Ecosystem" actions={
      <div className="flex gap-2 items-center">
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">Sandbox Mode</span>
        {tab==='abha'&&abhaPatient&&<><button onClick={()=>setShowCreateABHA(true)} className="btn-primary text-sm">🆔 Create ABHA</button><button onClick={()=>setShowLinkForm(true)} className="btn-secondary text-sm">🔗 Link ABHA</button></>}
        {tab==='consent'&&<button onClick={()=>setShowConsentForm(true)} className="btn-primary text-sm">📝 Request Consent</button>}
        {tab==='discovery'&&<button onClick={()=>setShowDiscForm(true)} className="btn-primary text-sm">🔍 Discover Records</button>}
        {tab==='hpr'&&<button onClick={()=>setShowHPRForm(true)} className="btn-primary text-sm">➕ Register HPR</button>}
      </div>
    }>
      {msg&&<div className={`mb-4 p-3 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':msg.startsWith('⚠️')?'bg-amber-50 text-amber-700 border-amber-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="ABHA Linked" value={stats.linkedPatients||0} icon="🆔" color="blue"/>
        <StatCard label="Active Consents" value={stats.activeArtefacts||0} icon="✅" color="green"/>
        <StatCard label="Records Shared" value={stats.documentsShared||0} icon="📤" color="purple"/>
        <StatCard label="Failed Transactions" value={stats.failedTransactions||0} icon="⚠️" color="red"/>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Pending Consents" value={stats.pendingConsents||0} icon="⏳" color="amber"/>
        <StatCard label="Pending Sharing" value={stats.pendingSharing||0} icon="📋" color="slate"/>
        <StatCard label="Granted Consents" value={stats.grantedConsents||0} icon="🤝" color="green"/>
        <StatCard label="Total ABHA" value={stats.totalABHA||0} icon="💠" color="blue"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b">
        {TABS.map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t as any)}
            className={`px-3 py-2 rounded-t-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab===t?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
        ))}
      </div>

      {/* DASHBOARD */}
      {tab==='dashboard'&&(
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="card p-5">
            <h3 className="font-semibold text-slate-700 mb-3">ABDM Overview</h3>
            <div className="space-y-3 text-sm">
              {[['Total ABHA Numbers',stats.totalABHA||0],['Linked to Patients',stats.linkedPatients||0],['Pending Consent Requests',stats.pendingConsents||0],['Active Consent Artefacts',stats.activeArtefacts||0],['Records Shared',stats.documentsShared||0],['Pending Sharing',stats.pendingSharing||0]].map(([l,v])=>(
                <div key={l as string} className="flex justify-between items-center">
                  <span className="text-slate-500">{l as string}</span>
                  <span className="font-semibold text-[#02357A]">{v as number}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card p-5">
            <h3 className="font-semibold text-slate-700 mb-3">Supported Health Information Types</h3>
            <div className="space-y-2">
              {[['💊','Prescription','Pharmacy / OPD prescriptions'],['🔬','DiagnosticReport','Lab test results'],['🏥','DischargeSummary','IPD discharge summaries'],['🩺','OPConsultation','OPD consultation records'],['💉','ImmunizationRecord','Vaccination records'],['❤️','WellnessRecord','Vitals and wellness data']].map(([icon,name,desc])=>(
                <div key={name as string} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                  <span className="text-xl">{icon as string}</span>
                  <div><p className="font-medium text-slate-700 text-sm">{name as string}</p><p className="text-xs text-slate-400">{desc as string}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ABHA MANAGEMENT */}
      {tab==='abha'&&(
        <div>
          <div className="card p-4 mb-5">
            <PatientSearchInput label="Select Patient to View / Manage ABHA" selected={abhaPatient}
              onSelect={(p:any)=>{setAbhaPatient(p);setAbhaRecord(null);}}
              onClear={()=>{setAbhaPatient(null);setAbhaRecord(null);}}/>
          </div>
          {abhaPatient&&(
            abhaRecord?(<div className="card p-5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl">🆔</div>
                <div>
                  <p className="text-xl font-bold text-[#02357A]">{abhaRecord.abhaNumber||abhaRecord.healthId}</p>
                  <p className="text-slate-500">{abhaRecord.abhaAddress}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${abhaRecord.status==='ACTIVE'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>{abhaRecord.status}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {[['Name',`${abhaRecord.firstName||''} ${abhaRecord.lastName||''}`],['Mobile',abhaRecord.mobile],['Gender',abhaRecord.gender],['DOB',abhaRecord.dateOfBirth||'—'],['State',abhaRecord.stateCode||'—']].map(([l,v])=>(
                  <div key={l as string} className="bg-slate-50 p-3 rounded"><p className="text-xs text-slate-400">{l as string}</p><p className="font-semibold">{v as string}</p></div>
                ))}
              </div>
            </div>)
            :(<div className="card p-8 text-center">
              <span className="text-5xl block mb-3">🆔</span>
              <p className="text-slate-500 font-medium mb-4">No ABHA linked to {abhaPatient.firstName}. Create a new ABHA or link an existing one.</p>
              <div className="flex gap-3 justify-center">
                <button onClick={()=>setShowCreateABHA(true)} className="btn-primary">🆔 Create ABHA (Aadhaar OTP)</button>
                <button onClick={()=>setShowLinkForm(true)} className="btn-secondary">🔗 Link Existing ABHA</button>
              </div>
            </div>)
          )}
        </div>
      )}

      {/* PATIENT LINKING */}
      {tab==='linking'&&(
        <div className="card overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center"><h3 className="font-semibold text-slate-700">ABHA Patient Links ({links.length})</h3></div>
          <DataTable loading={loading} emptyMessage="No ABHA-patient links" emptyIcon="🔗" data={links} columns={[
            {key:'patient',label:'Patient',render:(r:any)=><span>{r.patient_first_name||''} {r.patient_last_name||''}</span>},
            {key:'abha',label:'ABHA Number',render:(r:any)=><span className="font-mono text-sm font-semibold text-[#02357A]">{r.abhaNumber}</span>},
            {key:'addr',label:'ABHA Address',render:(r:any)=><span className="text-xs text-slate-500">{r.abhaAddress||'—'}</span>},
            {key:'method',label:'Link Method',render:(r:any)=><Badge label={r.linkMethod} className="bg-blue-50 text-[#02357A]"/>},
            {key:'date',label:'Link Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDateTime(r.linkDate)}</span>},
            {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.linkStatus==='LINKED'?'bg-green-100 text-green-700':r.linkStatus==='UNLINKED'?'bg-red-100 text-red-600':'bg-amber-100 text-amber-700'}`}>{r.linkStatus}</span>},
            {key:'actions',label:'',render:(r:any)=>r.linkStatus==='LINKED'&&<button onClick={async()=>{await api.unlinkAbha(r.id,{reason:'User requested unlink'});notify('✅ ABHA unlinked');loadTab();}} className="text-xs text-red-500 hover:underline">Unlink</button>},
          ]}/>
        </div>
      )}

      {/* CONSENTS */}
      {tab==='consent'&&(
        <div className="space-y-5">
          <div className="card overflow-hidden">
            <div className="p-4 border-b"><h3 className="font-semibold text-slate-700">Consent Requests ({consents.length})</h3></div>
            <DataTable loading={loading} emptyMessage="No consent requests" emptyIcon="✅" data={consents} columns={[
              {key:'req',label:'Request ID',render:(r:any)=><span className="font-mono text-xs">{r.consentRequestId||r.id}</span>},
              {key:'patient',label:'Patient',render:(r:any)=><span>{r.patient_first_name||''} {r.patient_last_name||''}</span>},
              {key:'abha',label:'ABHA',render:(r:any)=><span className="font-mono text-xs text-[#02357A]">{r.abhaNumber||'—'}</span>},
              {key:'purpose',label:'Purpose',render:(r:any)=><span className="text-xs">{PURPOSE_TEXT[r.purpose]||r.purpose}</span>},
              {key:'types',label:'HI Types',render:(r:any)=><div className="flex flex-wrap gap-1">{(r.hiTypes||[]).map((t:string)=><span key={t} className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{t}</span>)}</div>},
              {key:'expiry',label:'Expiry',render:(r:any)=><span className="text-xs text-slate-400">{r.expiryDate?fmtDate(r.expiryDate):'—'}</span>},
              {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${CONSENT_STATUS_COLORS[r.status]||'bg-slate-100 text-slate-500'}`}>{r.status}</span>},
              {key:'actions',label:'',render:(r:any)=>r.status==='GRANTED'&&<button onClick={async()=>{await api.getConsents({id:r.id});notify('Consent revoked!');loadTab();}} className="text-xs text-red-500 hover:underline">Revoke</button>},
            ]}/>
          </div>
          <div className="card overflow-hidden">
            <div className="p-4 border-b"><h3 className="font-semibold text-slate-700">Consent Artefacts — Active ({artefacts.length})</h3></div>
            <DataTable loading={loading} emptyMessage="No active artefacts" emptyIcon="🔑" data={artefacts} columns={[
              {key:'id',label:'Artefact ID',render:(r:any)=><span className="font-mono text-xs">{r.artefactId?.slice(0,16)||'—'}...</span>},
              {key:'abha',label:'Patient ABHA',render:(r:any)=><span className="font-mono text-xs text-[#02357A]">{r.patientAbha}</span>},
              {key:'purpose',label:'Purpose',render:(r:any)=><span className="text-xs">{PURPOSE_TEXT[r.purposeCode]||r.purposeCode}</span>},
              {key:'types',label:'HI Types',render:(r:any)=><div className="flex flex-wrap gap-1">{(r.hiTypes||[]).map((t:string)=><span key={t} className="text-xs bg-purple-50 text-purple-700 px-1 py-0.5 rounded">{t}</span>)}</div>},
              {key:'expiry',label:'Expiry',render:(r:any)=>{const d=new Date(r.expiry);const exp=d<new Date();return<span className={exp?'text-red-500 text-xs':'text-xs text-slate-400'}>{exp?'⚠️ ':''}{fmtDate(r.expiry)}</span>;}},
              {key:'status',label:'Status',render:(r:any)=><Badge label={r.status} status={r.status}/>},
              {key:'actions',label:'',render:(r:any)=>r.status==='ACTIVE'&&<button onClick={async()=>{await api.revokeConsentArtefact(r.artefactId);notify('✅ Artefact revoked');loadTab();}} className="text-xs text-red-500 hover:underline">Revoke</button>},
            ]}/>
          </div>
        </div>
      )}

      {/* HEALTH RECORDS */}
      {tab==='records'&&(
        <div className="space-y-4">
          <div className="card p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2"><PatientSearchInput label="Patient" selected={recordPatient} onSelect={(p:any)=>setRecordPatient(p)} onClear={()=>{setRecordPatient(null);setRecords([]);}}/></div>
              <div>
                <label className="label">Health Information Type</label>
                <select className="input" value={hiType} onChange={e=>setHiType(e.target.value)}>
                  <option value="OPConsultation">🩺 OPD Consultation</option>
                  <option value="Prescription">💊 Prescription</option>
                  <option value="DiagnosticReport">🔬 Diagnostic Report</option>
                  <option value="DischargeSummary">🏥 Discharge Summary</option>
                  <option value="ImmunizationRecord">💉 Immunization Record</option>
                  <option value="WellnessRecord">❤️ Wellness Record</option>
                  <option value="HealthDocumentRecord">📁 Health Document</option>
                </select>
              </div>
            </div>
          </div>

          {/* Info bar — records come from HMIS modules */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-4 items-start">
            <span className="text-[#02357A] text-lg mt-0.5">💡</span>
            <div className="flex-1">
              <p className="text-xs font-semibold text-blue-800 mb-1">Records are created in their HMIS modules — select a type then click the module link to create records there.</p>
              <div className="flex flex-wrap gap-1.5">
                {([
                  ['OPConsultation','🩺 OPD Module','/opd'],
                  ['Prescription','💊 Doctor EMR','/doctor/emr'],
                  ['DiagnosticReport','🔬 Laboratory','/laboratory'],
                  ['DischargeSummary','🏥 IPD','/ipd'],
                  ['ImmunizationRecord','💉 OPD (Immunization)','/opd'],
                  ['WellnessRecord','❤️ Doctor EMR (Vitals)','/doctor/emr'],
                  ['HealthDocumentRecord','📁 Document Management','/dms'],
                ] as [string,string,string][]).map(([type,label,path])=>(
                  <button key={type}
                    onClick={()=>{setHiType(type);window.open(path,'_blank');}}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${hiType===type?'bg-[#02357A] text-white border-[#015CA6]':'bg-white text-[#02357A] border-blue-200 hover:border-blue-400'}`}>
                    {label} ↗
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Records list pulled from HMIS */}
          <div className="card overflow-hidden">
            <div className="p-3 border-b flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-slate-700 text-sm">
                  {{
                    OPConsultation:'🩺 OPD Consultations',Prescription:'💊 Prescriptions',
                    DiagnosticReport:'🔬 Lab / Diagnostic Reports',DischargeSummary:'🏥 Discharge Summaries',
                    ImmunizationRecord:'💉 Immunization Records',WellnessRecord:'❤️ Wellness / Vitals Records',
                    HealthDocumentRecord:'📁 Health Documents',
                  }[hiType]||hiType}
                  {recordPatient&&<span className="text-slate-400 font-normal text-xs ml-2">— {recordPatient.first_name||recordPatient.firstName} {recordPatient.last_name||recordPatient.lastName||''} ({recordPatient.uhid})</span>}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Select a record and click "📤 Push to ABDM" to link it to the patient's ABHA account</p>
              </div>
              {records.length>0&&<span className="text-xs bg-blue-50 text-[#02357A] px-2 py-1 rounded-full">{records.length} record(s)</span>}
            </div>
            <DataTable loading={loading}
              emptyMessage={recordPatient
                ?`No ${hiType} records found for this patient in HMIS. Create records in the ${
                  {OPConsultation:'OPD Module',Prescription:'Doctor EMR',DiagnosticReport:'Laboratory',
                   DischargeSummary:'IPD Module',ImmunizationRecord:'OPD (Immunization)',
                   WellnessRecord:'Doctor EMR (Vitals)',HealthDocumentRecord:'Document Management'}[hiType]||'relevant module'
                } first, then return here to push to ABDM.`
                :'Select a patient above to see their health records from HMIS'}
              emptyIcon="📋"
              data={records}
              columns={[
                {key:'record',label:'Record',render:(r:any)=>(
                  <div>
                    <p className="font-medium text-sm">{r.final_diagnosis||r.vaccine_name||r.request_number||r.summary_number||r.prescription_number||r.document_name||r.title||'Record'}</p>
                    {(r.provisional_diagnosis||r.chief_complaint)&&<p className="text-xs text-slate-400">{r.provisional_diagnosis||r.chief_complaint}</p>}
                  </div>
                )},
                {key:'date',label:'Date',render:(r:any)=><span className="text-xs text-slate-500">{fmtDate(r.date||r.created_at||r.createdAt||r.date_administered||r.consultation_date||r.discharge_date||r.prescription_date)}</span>},
                {key:'ref',label:'Reference #',render:(r:any)=><span className="font-mono text-xs text-[#02357A]">{r.request_number||r.summary_number||r.prescription_number||r.id?.slice(0,8)||'—'}</span>},
                {key:'abdmStatus',label:'ABDM Status',render:(r:any)=>(
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.pushStatus==='PUSHED'||r.status==='PUSHED'?'bg-green-100 text-green-700':r.status==='COMPLETED'||r.status==='ACTIVE'||r.status==='ADMINISTERED'?'bg-slate-100 text-slate-600':'bg-amber-100 text-amber-700'}`}>
                    {r.pushStatus==='PUSHED'?'✅ Pushed to ABDM':r.status==='PUSHED'?'✅ Pushed':'⏳ Not yet pushed'}
                  </span>
                )},
                {key:'actions',label:'',render:(r:any)=>(r.pushStatus||r.status)!=='PUSHED'&&(
                  <div className="flex gap-2">
                    <button onClick={()=>previewFhir(r)} disabled={!!fhirLoading}
                      className="text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1 rounded-lg transition-all font-medium disabled:opacity-50">
                      {fhirLoading?'⏳':'🔍'} Preview FHIR R4
                    </button>
                    <button onClick={()=>pushRecord(r.id||r.referenceId)} disabled={pushingRecord===r.id}
                      className="text-xs bg-[#02357A] text-white px-3 py-1 rounded-lg hover:bg-[#015CA6] disabled:opacity-50 transition-all whitespace-nowrap">
                      {pushingRecord===r.id?'⏳ Pushing...':'📤 Push to ABDM'}
                    </button>
                  </div>
                )},
              ]}
            />
          </div>
        </div>
      )}

      {/* DISCOVERY */}
      {tab==='discovery'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No discovery requests" emptyIcon="🔍" data={discoveries} columns={[
            {key:'id',label:'Request ID',render:(r:any)=><span className="font-mono text-xs">{r.discoveryRequestId||'—'}</span>},
            {key:'abha',label:'ABHA',render:(r:any)=><span className="font-mono text-xs text-[#02357A]">{r.abhaNumber||'—'}</span>},
            {key:'date',label:'Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDateTime(r.requestDate)}</span>},
            {key:'types',label:'HI Types',render:(r:any)=><div className="flex flex-wrap gap-1">{(r.hiTypesRequested||[]).map((t:string)=><span key={t} className="text-xs bg-blue-50 text-[#02357A] px-1 rounded">{t}</span>)}</div>},
            {key:'matched',label:'Matched',render:(r:any)=><span className={`font-bold ${r.matchingRecordsCount>0?'text-green-600':'text-slate-400'}`}>{r.matchingRecordsCount} records</span>},
            {key:'hip',label:'HIP Source',render:(r:any)=><span className="text-xs text-slate-500">{r.hipSource||'—'}</span>},
            {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status==='MATCHED'?'bg-green-100 text-green-700':r.status==='NO_MATCH'?'bg-amber-100 text-amber-700':r.status==='FAILED'?'bg-red-100 text-red-700':'bg-slate-100 text-slate-500'}`}>{r.status}</span>},
          ]}/>
        </div>
      )}

      {/* SHARING */}
      {tab==='sharing'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No record sharing history" emptyIcon="📤" data={sharing} columns={[
            {key:'id',label:'Transfer ID',render:(r:any)=><span className="font-mono text-xs">{r.transferId?.slice(0,12)||'—'}</span>},
            {key:'type',label:'Record Type',render:(r:any)=><Badge label={r.recordType||'—'} className="bg-purple-50 text-purple-700"/>},
            {key:'hiu',label:'HIU',render:(r:any)=><span className="text-xs">{r.hiuId||'—'}</span>},
            {key:'date',label:'Transfer Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDateTime(r.transferDate)}</span>},
            {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status==='SHARED'?'bg-green-100 text-green-700':r.status==='FAILED'?'bg-red-100 text-red-700':'bg-amber-100 text-amber-700'}`}>{r.status}</span>},
            {key:'retry',label:'Retries',render:(r:any)=>r.retryCount>0?<span className="text-amber-600 text-xs">{r.retryCount}×</span>:<span className="text-slate-300 text-xs">—</span>},
          ]}/>
        </div>
      )}

      {/* FACILITY REGISTRY */}
      {tab==='facility'&&(
        <div className="card overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center"><h3 className="font-semibold text-slate-700">Facility Registry</h3><span className="text-xs text-slate-400">NHA Health Facility Registry (HFR) data</span></div>
          <DataTable loading={loading} emptyMessage="No facilities registered — sync from NHA HFR" emptyIcon="🏥" data={facilities} columns={[
            {key:'fid',label:'Facility ID',render:(r:any)=><span className="font-mono text-xs">{r.facilityId}</span>},
            {key:'name',label:'Facility',render:(r:any)=><div><p className="font-medium">{r.facilityName}</p><p className="text-xs text-slate-400">{r.facilityType} · {r.ownershipType}</p></div>},
            {key:'location',label:'Location',render:(r:any)=><span className="text-xs text-slate-500">{r.district}, {r.state}</span>},
            {key:'hip',label:'HIP',render:(r:any)=>r.isHipRegistered?<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✅ HIP</span>:<span className="text-xs text-slate-400">—</span>},
            {key:'hiu',label:'HIU',render:(r:any)=>r.isHiuRegistered?<span className="text-xs bg-blue-100 text-[#02357A] px-2 py-0.5 rounded-full">✅ HIU</span>:<span className="text-xs text-slate-400">—</span>},
            {key:'status',label:'Status',render:(r:any)=><Badge label={r.registrationStatus} status={r.registrationStatus}/>},
          ]}/>
        </div>
      )}

      {/* HPR */}
      {tab==='hpr'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No HPR registrations" emptyIcon="👨‍⚕️" data={hprList} columns={[
            {key:'hpr',label:'HPR ID',render:(r:any)=><span className="font-mono text-sm text-[#02357A]">{r.hprId||'—'}</span>},
            {key:'doctor',label:'Doctor',render:(r:any)=><span>Dr. {r.doctor_first_name||''} {r.doctor_last_name||''}</span>},
            {key:'reg',label:'Registration #',render:(r:any)=><span className="font-mono text-xs">{r.registrationNumber||'—'}</span>},
            {key:'council',label:'Council',render:(r:any)=><span className="text-xs text-slate-500">{r.registrationCouncil||'—'}</span>},
            {key:'spec',label:'Speciality',render:(r:any)=><span className="text-xs">{r.speciality||'—'}</span>},
            {key:'qual',label:'Qualification',render:(r:any)=><span className="text-xs text-slate-500">{r.qualification||'—'}</span>},
            {key:'status',label:'Status',render:(r:any)=><Badge label={r.hprStatus} status={r.hprStatus}/>},
          ]}/>
        </div>
      )}

      {/* AUDIT LOGS */}
      {tab==='logs'&&(
        <div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {['','SUCCESS','FAILED'].map(s=><button key={s} onClick={()=>setLogFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${logFilter===s?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{s||'All'}</button>)}
          </div>
          <div className="card overflow-hidden">
            <DataTable loading={loading} emptyMessage="No transaction logs" emptyIcon="📋" data={logs} columns={[
              {key:'type',label:'Transaction',render:(r:any)=><Badge label={r.transactionType.replace('_',' ')} className="bg-blue-50 text-[#02357A]"/>},
              {key:'dir',label:'Direction',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full ${r.direction==='OUTBOUND'?'bg-slate-100 text-slate-600':'bg-purple-50 text-purple-700'}`}>{r.direction}</span>},
              {key:'abha',label:'ABHA',render:(r:any)=><span className="font-mono text-xs text-[#02357A]">{r.abhaNumber||'—'}</span>},
              {key:'date',label:'Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDateTime(r.created_at||r.createdAt)}</span>},
              {key:'code',label:'HTTP',render:(r:any)=><span className={`text-xs font-mono ${r.responseCode>=400?'text-red-600':r.responseCode>=200?'text-green-600':'text-slate-400'}`}>{r.responseCode||'—'}</span>},
              {key:'duration',label:'Duration',render:(r:any)=><span className="text-xs text-slate-400">{r.durationMs||'—'}ms</span>},
              {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status==='SUCCESS'?'bg-green-100 text-green-700':r.status==='FAILED'?'bg-red-100 text-red-700':'bg-amber-100 text-amber-700'}`}>{r.status}</span>},
              {key:'err',label:'Error',render:(r:any)=>r.errorMessage?<span className="text-xs text-red-500 line-clamp-1">{r.errorMessage}</span>:<span className="text-slate-300 text-xs">—</span>},
            ]}/>
          </div>
        </div>
      )}

      {/* ─── MODALS ─── */}

      {/* Create ABHA (Aadhaar OTP flow) */}
      <SlidePanel isOpen={showCreateABHA} onClose={()=>{setShowCreateABHA(false);setAbhaStep('init');}} title="Create ABHA — Aadhaar OTP" size="md">
        {abhaStep==='init'&&(
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-[#02357A]">This initiates the ABHA creation flow via NHA sandbox. In production, requires valid ABDM credentials in .env.</div>
            <div><label className="label">Patient: {abhaPatient?.firstName} {abhaPatient?.lastName}</label></div>
            <div><label className="label">Aadhaar Number *</label><Input value={aadhaar} onChange={e=>setAadhaar(e.target.value)} placeholder="12-digit Aadhaar" maxLength={12}/></div>
            <div><label className="label">Mobile Number *</label><Input type="tel" value={mobile} onChange={e=>setMobile(e.target.value)} placeholder="10-digit mobile"/></div>
            <div className="flex gap-3"><button onClick={generateOTP} disabled={saving||!aadhaar||!mobile} className="btn-primary flex-1">{saving?'Sending OTP...':'Send OTP'}</button><button onClick={()=>setShowCreateABHA(false)} className="btn-secondary">Cancel</button></div>
          </div>
        )}
        {abhaStep==='otp'&&(
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">OTP sent to {mobile}. Enter the 6-digit OTP to proceed.</div>
            <div><label className="label">Enter OTP *</label><Input value={otp} onChange={e=>setOtp(e.target.value)} placeholder="6-digit OTP" maxLength={6} className="text-center text-xl tracking-widest font-bold"/></div>
            <div className="flex gap-3"><button onClick={verifyOTP} disabled={saving||otp.length<6} className="btn-primary flex-1">{saving?'Verifying...':'Verify OTP'}</button><button onClick={()=>setAbhaStep('init')} className="btn-secondary">Back</button></div>
          </div>
        )}
        {abhaStep==='create'&&(
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">✅ OTP verified! Click below to create the ABHA number and link it to the patient.</div>
            <div className="flex gap-3"><button onClick={createABHA} disabled={saving} className="btn-primary flex-1">{saving?'Creating ABHA...':'Create & Link ABHA'}</button></div>
          </div>
        )}
      </SlidePanel>

      {/* Link Existing ABHA */}
      <SlidePanel isOpen={showLinkForm} onClose={()=>setShowLinkForm(false)} title="Link Existing ABHA" size="sm">
        <form onSubmit={linkExistingABHA} className="space-y-3">
          <div><label className="label">Patient: {abhaPatient?.firstName} {abhaPatient?.lastName}</label></div>
          <div><label className="label">ABHA Number *</label><Input value={linkForm.abhaNumber} onChange={e=>setLinkForm({...linkForm,abhaNumber:e.target.value})} placeholder="14-digit ABHA number" required/></div>
          <div><label className="label">ABHA Address</label><Input value={linkForm.abhaAddress} onChange={e=>setLinkForm({...linkForm,abhaAddress:e.target.value})} placeholder="e.g. patient@abdm"/></div>
          <div><label className="label">Link Method</label><Select value={linkForm.linkMethod} onChange={e=>setLinkForm({...linkForm,linkMethod:e.target.value})}>{['OTP','AADHAAR','DEMOGRAPHICS','MANUAL'].map(m=><option key={m}>{m}</option>)}</Select></div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Linking...':'Link ABHA'}</button><button type="button" onClick={()=>setShowLinkForm(false)} className="btn-secondary">Cancel</button></div>
        </form>
      </SlidePanel>

      {/* Consent Request */}
      <SlidePanel isOpen={showConsentForm} onClose={()=>setShowConsentForm(false)} title="Request Consent" size="lg">
        <form onSubmit={submitConsent} className="space-y-3">
          <PatientSearchInput label="Patient *" selected={consentPatient}
            onSelect={(p:any)=>{setConsentPatient(p);setConsentForm({...consentForm,patientId:p.id});}}
            onClear={()=>{setConsentPatient(null);setConsentForm({...consentForm,patientId:''});}}/>
          <div><label className="label">ABHA Number / Address</label><Input value={consentForm.abhaNumber} onChange={e=>setConsentForm({...consentForm,abhaNumber:e.target.value})} placeholder="Patient's ABHA number or @abdm address"/></div>
          <div><label className="label">Purpose *</label>
            <Select value={consentForm.purposeCode} onChange={e=>setConsentForm({...consentForm,purposeCode:e.target.value})}>
              {PURPOSE_CODES.map(p=><option key={p} value={p}>{PURPOSE_TEXT[p]||p}</option>)}
            </Select>
          </div>
          <div>
            <label className="label mb-2">Health Information Types</label>
            <div className="grid grid-cols-3 gap-2">
              {HI_TYPES.map(t=>(
                <label key={t} className={`flex items-center gap-2 p-2 border rounded text-xs cursor-pointer ${consentForm.hiTypes.includes(t)?'border-blue-400 bg-blue-50':'border-slate-200 hover:border-blue-200'}`}>
                  <input type="checkbox" checked={consentForm.hiTypes.includes(t)} onChange={e=>setConsentForm({...consentForm,hiTypes:e.target.checked?[...consentForm.hiTypes,t]:consentForm.hiTypes.filter(x=>x!==t)})} className="w-3 h-3"/>
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="label">Date From</label><Input type="date" value={consentForm.dateFrom} onChange={e=>setConsentForm({...consentForm,dateFrom:e.target.value})}/></div>
            <div><label className="label">Date To</label><Input type="date" value={consentForm.dateTo} onChange={e=>setConsentForm({...consentForm,dateTo:e.target.value})}/></div>
            <div><label className="label">Consent Expiry</label><Input type="date" value={consentForm.expiryDate} onChange={e=>setConsentForm({...consentForm,expiryDate:e.target.value})}/></div>
          </div>
          <div className="flex gap-3"><button type="submit" disabled={saving||!consentForm.patientId||!consentForm.hiTypes.length} className="btn-primary flex-1">{saving?'Sending...':'Send Consent Request'}</button><button type="button" onClick={()=>setShowConsentForm(false)} className="btn-secondary">Cancel</button></div>
        </form>
      </SlidePanel>

      {/* Discovery Form */}
      <SlidePanel isOpen={showDiscForm} onClose={()=>setShowDiscForm(false)} title="Discover Health Records" size="md">
        <form onSubmit={submitDiscovery} className="space-y-3">
          <PatientSearchInput label="Patient" selected={discPatient} onSelect={(p:any)=>{setDiscPatient(p);setDiscForm({...discForm,patientId:p.id});}} onClear={()=>{setDiscPatient(null);setDiscForm({...discForm,patientId:'',abhaNumber:'',});}}/>
          <div><label className="label">ABHA Number</label><Input value={discForm.abhaNumber} onChange={e=>setDiscForm({...discForm,abhaNumber:e.target.value})} placeholder="14-digit ABHA"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Date From</label><Input type="date" value={discForm.dateFrom} onChange={e=>setDiscForm({...discForm,dateFrom:e.target.value})}/></div>
            <div><label className="label">Date To</label><Input type="date" value={discForm.dateTo} onChange={e=>setDiscForm({...discForm,dateTo:e.target.value})}/></div>
          </div>
          <div>
            <label className="label mb-2">HI Types to Discover</label>
            <div className="grid grid-cols-3 gap-2">
              {HI_TYPES.map(t=>(
                <label key={t} className={`flex items-center gap-1.5 p-2 border rounded text-xs cursor-pointer ${discForm.hiTypes.includes(t)?'border-blue-400 bg-blue-50':'border-slate-200'}`}>
                  <input type="checkbox" checked={discForm.hiTypes.includes(t)} onChange={e=>setDiscForm({...discForm,hiTypes:e.target.checked?[...discForm.hiTypes,t]:discForm.hiTypes.filter(x=>x!==t)})} className="w-3 h-3"/>
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Sending...':'Initiate Discovery'}</button><button type="button" onClick={()=>setShowDiscForm(false)} className="btn-secondary">Cancel</button></div>
        </form>
      </SlidePanel>

      {/* HPR Registration */}
      <SlidePanel isOpen={showHPRForm} onClose={()=>setShowHPRForm(false)} title="Register Healthcare Professional (HPR)" size="md">
        <form onSubmit={submitHPR} className="space-y-3">
          <div><label className="label">Doctor *</label><Select value={hprForm.doctorId} onChange={e=>setHprForm({...hprForm,doctorId:e.target.value})} required><option value="">Select Doctor</option>{doctors.map((d:any)=><option key={d.id} value={d.id}>Dr. {d.firstName} {d.lastName}</option>)}</Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">HPR ID</label><Input value={hprForm.hprId} onChange={e=>setHprForm({...hprForm,hprId:e.target.value})} placeholder="HPR registered ID"/></div>
            <div><label className="label">Registration #</label><Input value={hprForm.registrationNumber} onChange={e=>setHprForm({...hprForm,registrationNumber:e.target.value})}/></div>
            <div><label className="label">Registration Council</label><Select value={hprForm.registrationCouncil} onChange={e=>setHprForm({...hprForm,registrationCouncil:e.target.value})}>{['Medical Council of India','State Medical Council','Dental Council of India','Nursing Council','Pharmacy Council'].map(c=><option key={c}>{c}</option>)}</Select></div>
            <div><label className="label">Speciality</label><Input value={hprForm.speciality} onChange={e=>setHprForm({...hprForm,speciality:e.target.value})} placeholder="e.g. General Medicine"/></div>
            <div className="col-span-2"><label className="label">Qualification</label><Input value={hprForm.qualification} onChange={e=>setHprForm({...hprForm,qualification:e.target.value})} placeholder="e.g. MBBS, MD"/></div>
          </div>
          <div className="flex gap-3"><button type="submit" disabled={saving||!hprForm.doctorId} className="btn-primary flex-1">{saving?'Registering...':'Register HPR'}</button><button type="button" onClick={()=>setShowHPRForm(false)} className="btn-secondary">Cancel</button></div>
        </form>
      </SlidePanel>

      {/* FHIR R4 Bundle Viewer */}
      {showFhirViewer&&(
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50" onClick={()=>setShowFhirViewer(false)}/>
          <div className="w-[60vw] max-w-4xl bg-white h-full flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-700 to-[#015CA6] text-white p-4 flex justify-between items-start flex-shrink-0">
              <div>
                <p className="font-bold text-lg">🔬 FHIR R4 Bundle</p>
                <p className="text-xs opacity-80 mt-0.5">{fhirRecordTitle}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">resourceType: Bundle</span>
                  <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">type: document</span>
                  <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">ABDM NRCES v1.0</span>
                  <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">{fhirBundle?.entry?.length||0} resources</span>
                </div>
              </div>
              <button onClick={()=>setShowFhirViewer(false)} className="text-white/80 hover:text-white text-2xl ml-4">✕</button>
            </div>

            {/* Resource Summary */}
            {fhirBundle&&(
              <div className="flex gap-2 p-3 bg-indigo-50 border-b overflow-x-auto flex-shrink-0">
                {(fhirBundle.entry||[]).map((e:any,i:number)=>{
                  const rt=e.resource?.resourceType||'Unknown';
                  const COLOR:Record<string,string>={Bundle:'bg-indigo-100 text-indigo-700',Composition:'bg-blue-100 text-[#02357A]',Patient:'bg-teal-100 text-teal-700',Encounter:'bg-green-100 text-green-700',Condition:'bg-red-100 text-red-700',MedicationRequest:'bg-amber-100 text-amber-700',DiagnosticReport:'bg-purple-100 text-purple-700',Observation:'bg-slate-100 text-slate-700',Immunization:'bg-orange-100 text-orange-700',Practitioner:'bg-cyan-100 text-cyan-700',Organization:'bg-gray-100 text-gray-700'};
                  return<span key={i} className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap font-medium ${COLOR[rt]||'bg-slate-100 text-slate-600'}`}>{rt}</span>;
                })}
              </div>
            )}

            {/* Resource Detail Tabs */}
            {fhirBundle&&(
              <div className="flex-1 overflow-hidden flex flex-col">
                {/* Resource List + JSON toggle */}
                <div className="flex border-b bg-slate-50">
                  <span className="text-xs text-slate-500 px-3 py-2 font-semibold">Resources</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {/* Composition first */}
                  {(fhirBundle.entry||[]).map((entry:any,idx:number)=>{
                    const r=entry.resource;
                    if(!r) return null;
                    const rt=r.resourceType;
                    return(
                      <details key={idx} className="border rounded-xl overflow-hidden">
                        <summary className="flex items-center gap-3 p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 select-none">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${rt==='Composition'?'bg-blue-100 text-[#02357A]':rt==='Patient'?'bg-teal-100 text-teal-700':rt==='Condition'?'bg-red-100 text-red-700':rt==='MedicationRequest'?'bg-amber-100 text-amber-700':rt==='Observation'?'bg-slate-200 text-slate-700':rt==='DiagnosticReport'?'bg-purple-100 text-purple-700':rt==='Practitioner'?'bg-cyan-100 text-cyan-700':'bg-slate-100 text-slate-600'}`}>{rt}</span>
                          <span className="text-sm font-medium text-slate-700 flex-1">
                            {rt==='Patient'&&`${r.name?.[0]?.text||r.name?.[0]?.family||''} ${r.name?.[0]?.given?.[0]||''}`}
                            {rt==='Composition'&&(r.title||r.type?.coding?.[0]?.display||'Composition')}
                            {rt==='Condition'&&(r.code?.text||r.code?.coding?.[0]?.display||'Condition')}
                            {rt==='MedicationRequest'&&(r.medicationCodeableConcept?.text||r.medicationCodeableConcept?.coding?.[0]?.display||'Medication')}
                            {rt==='Observation'&&(r.code?.text||r.code?.coding?.[0]?.display||'Observation')}
                            {rt==='DiagnosticReport'&&(r.code?.text||r.code?.coding?.[0]?.display||'DiagnosticReport')}
                            {rt==='Practitioner'&&(r.name?.[0]?.text||r.name?.[0]?.family||'Practitioner')}
                            {rt==='Immunization'&&(r.vaccineCode?.text||r.vaccineCode?.coding?.[0]?.display||'Immunization')}
                            {!['Patient','Composition','Condition','MedicationRequest','Observation','DiagnosticReport','Practitioner','Immunization'].includes(rt)&&(r.id||entry.fullUrl||rt)}
                          </span>
                          {r.id&&<span className="text-xs font-mono text-slate-400">{r.id.slice(0,12)}…</span>}
                        </summary>
                        <div className="p-3">
                          <pre className="text-xs bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">{JSON.stringify(r,null,2)}</pre>
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="p-4 border-t bg-slate-50 flex gap-3 flex-shrink-0">
              <button onClick={()=>{const blob=new Blob([JSON.stringify(fhirBundle,null,2)],{type:'application/json'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=`FHIR_${hiType}_${Date.now()}.json`;a.click();}} className="btn-secondary text-sm">⬇️ Download FHIR JSON</button>
              <button onClick={()=>{if(recordPatient)pushRecord(records.find((r:any)=>fhirBundle)?.id||'');setShowFhirViewer(false);}} className="btn-primary text-sm flex-1">📤 Push this bundle to ABDM</button>
              <button onClick={()=>setShowFhirViewer(false)} className="btn-secondary text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );

}
